import { useCallback, useMemo } from 'react';
import * as Sentry from '@sentry/react';
import {
  type ApolloError,
  ConversationType,
  MessagePartType,
  useAskMutation,
  useCreateConversationMutation,
} from '@matrix-forge/graphql-client';
import { toast } from '@matrix-forge/ui/components/sonner';
import { formatDate } from '../utils/datetime';
import { AllClientSideTools } from '../tools/all';
import type { ClientTool } from './useClientTools';

export type UseNewChatOptions = {
  onNewChat?: ((args: { conversationId: string; agentId: string }) => any) | null;
  /** Additional client tools to include (e.g. persona tools for Agent-type agents). */
  extraClientTools?: ClientTool[];
};

/**
 * Creates a new conversation with a given agent id and asks a given prompt.
 *
 * This is a convenient wrapper for two mutations, useCreateConversationMutation and useAskMutation.
 * If successful, invokes the given callback `onNewChat` with the newly created conversation id,
 * the prompt text that was asked, and the agent id it was directed to.
 */
export const useNewChat = (opts?: UseNewChatOptions | null) => {
  const onNewChat = opts?.onNewChat ?? null;
  const extraClientTools = opts?.extraClientTools;

  const allTools = useMemo(
    () =>
      extraClientTools?.length
        ? [...AllClientSideTools, ...extraClientTools]
        : AllClientSideTools,
    [extraClientTools]
  );

  const [createConversation, createState] = useCreateConversationMutation();
  const [ask, askState] = useAskMutation();
  const trigger = useCallback(
    async ({ agentId, prompt }: { agentId: string; prompt: string }) => {
      try {
        // Prepare client tools for registration
        const clientTools = allTools.map(tool => ({
          name: tool.name,
          description: tool.description,
          parameters: JSON.stringify(tool.parameters), // JSON scalar requires string
        }));

        // Create a new conversation with client tools
        const createResponse = await createConversation({
          variables: {
            input: {
              title: `Chat started ${formatDate(new Date())}`,
              type: ConversationType.Chat,
              clientTools,
            },
          },
        });

        // Validate the newly created conversation id.
        const newConversationId = createResponse?.data?.createConversation?.id;
        if (!newConversationId) {
          throw Error('Invalid response when creating new conversation.');
        }
        // Submit the prompt to the given agent in this new conversation.
        return ask({
          variables: {
            input: {
              conversationId: newConversationId,
              agentId,
              clientTools,
              parts: [{ type: MessagePartType.Text, text: prompt ?? '' }],
            },
          },
        }).then(
          // Prompt submitted successfully. Return new conversation id to caller.
          () => {
            const result = { conversationId: newConversationId, agentId, prompt };
            onNewChat?.(result);
            return result;
          },
          // Prompt submission failed.
          error => {
            throw error;
          }
        );
      } catch (error) {
        const err = error as ApolloError;
        toast.error('Something went wrong starting a new chat.', {
          id: 'new-chat',
          description: `${String(err?.message || '')}${err?.message ? ' ' : ''}Please try again.`,
        });
        Sentry.captureException(error, {
          tags: {
            errorType: 'mutation',
            mutationType: 'createConversation | ask',
            component: 'useNewChat',
          },
          extra: {
            mutationVariables: {
              agentId,
              prompt,
            },
          },
        });
        throw error;
      }
    },
    [createConversation, ask, allTools]
  );
  const error = createState.error || askState.error;
  const loading = !error && !!(createState.loading || askState.loading);
  return useMemo<[typeof trigger, { loading: boolean; error?: ApolloError }]>(
    () => [trigger, { loading, error }],
    [trigger, loading, error]
  );
};
